/**
 * V38.39 — Panneau Super Admin « Menus front-office » (refonte UX).
 *
 * Améliorations :
 *  - Affichage : chips d'icônes colorées, menus repliables, hiérarchie claire,
 *    badges audience/planification.
 *  - Drag-and-drop (@dnd-kit) pour réordonner les menus ET les liens (poignée).
 *  - Sélection multiple + barre d'actions de masse (Afficher / Masquer / Supprimer).
 *
 * La navbar publique (`Navigation.js`) lit la config via /api/public/frontoffice-nav.
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import {
  Eye, EyeOff, Pencil, Trash2, Plus, Save, Layers, Loader2, ExternalLink, Clock, Lock,
  GripVertical, ChevronDown, ChevronRight, X,
  Sparkles, Zap, Shield, Building2, Leaf, Truck, GraduationCap, Users, Landmark, Target, Heart, Home, Store,
} from 'lucide-react';
import {
  DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, useSortable, verticalListSortingStrategy, arrayMove, sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import InstitutionalHero from './ui/InstitutionalHero';
import { NavItemChildrenEditor } from './NavItemChildrenEditor';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICONS = ['Sparkles', 'Zap', 'Shield', 'Building2', 'Leaf', 'Truck', 'GraduationCap', 'Users', 'Landmark', 'Target', 'Heart', 'Home', 'Store'];
const COLORS = ['blue', 'purple', 'green', 'emerald', 'teal', 'cyan', 'amber', 'orange', 'red', 'pink', 'slate'];
const AUDIENCES = [
  { value: 'all', label: 'Tout le monde' },
  { value: 'guests', label: 'Visiteurs (non connectés)' },
  { value: 'authenticated', label: 'Connectés (tous rôles)' },
  { value: 'roles', label: 'Rôles précis…' },
];
const ROLES = ['public', 'prospect', 'member', 'beneficiaire_pro', 'beneficiaire_particulier', 'cooper', 'expert', 'admin', 'super_admin'];

const ICON_MAP = { Sparkles, Zap, Shield, Building2, Leaf, Truck, GraduationCap, Users, Landmark, Target, Heart, Home, Store };
const ICON_BG = {
  blue: 'bg-blue-100', purple: 'bg-purple-100', green: 'bg-green-100', emerald: 'bg-emerald-100',
  teal: 'bg-teal-100', cyan: 'bg-cyan-100', amber: 'bg-amber-100', orange: 'bg-orange-100',
  red: 'bg-red-100', pink: 'bg-pink-100', slate: 'bg-slate-100',
};
const ICON_FG = {
  blue: 'text-blue-600', purple: 'text-purple-600', green: 'text-green-600', emerald: 'text-emerald-600',
  teal: 'text-teal-600', cyan: 'text-cyan-600', amber: 'text-amber-600', orange: 'text-orange-600',
  red: 'text-red-600', pink: 'text-pink-600', slate: 'text-slate-600',
};

const getAuthHeader = () => {
  const t = localStorage.getItem('token') || localStorage.getItem('access_token') || localStorage.getItem('admin_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};
const toLocalInput = (v) => (v ? String(v).slice(0, 16) : '');

const IconChip = ({ icon, color, size = 'md' }) => {
  const Ico = ICON_MAP[icon] || Sparkles;
  const dim = size === 'sm' ? 'w-7 h-7' : 'w-9 h-9';
  const idim = size === 'sm' ? 'w-3.5 h-3.5' : 'w-4 h-4';
  return (
    <div className={`${dim} rounded-lg flex items-center justify-center shrink-0 ${ICON_BG[color] || ICON_BG.slate}`}>
      <Ico className={`${idim} ${ICON_FG[color] || ICON_FG.slate}`} />
    </div>
  );
};

function VisBadges({ node }) {
  const aud = node.audience || 'all';
  const scheduled = !!(node.visible_from || node.visible_until);
  const audLabel = aud === 'roles' ? ((node.roles || []).join(', ') || 'rôles')
    : aud === 'guests' ? 'visiteurs' : aud === 'authenticated' ? 'connectés' : '';
  return (
    <>
      {aud !== 'all' && (
        <Badge variant="outline" className="text-[9px] gap-0.5 border-indigo-300 text-indigo-700">
          <Lock className="h-2.5 w-2.5" />{audLabel}
        </Badge>
      )}
      {scheduled && (
        <Badge variant="outline" className="text-[9px] gap-0.5 border-amber-300 text-amber-700">
          <Clock className="h-2.5 w-2.5" />planifié
        </Badge>
      )}
    </>
  );
}

function VisibilityFields({ form, setForm }) {
  const toggleRole = (r) => {
    const cur = form.roles || [];
    setForm({ ...form, roles: cur.includes(r) ? cur.filter((x) => x !== r) : [...cur, r] });
  };
  return (
    <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
      <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Visibilité avancée</p>
      <div>
        <Label className="text-xs">Audience</Label>
        <select value={form.audience || 'all'} onChange={(e) => setForm({ ...form, audience: e.target.value })} className="w-full h-9 rounded-md border border-slate-300 px-3 text-sm bg-white" data-testid="vis-audience">
          {AUDIENCES.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
        </select>
      </div>
      {form.audience === 'roles' && (
        <div className="flex flex-wrap gap-1.5" data-testid="vis-roles">
          {ROLES.map((r) => (
            <button key={r} type="button" onClick={() => toggleRole(r)}
              className={`text-[11px] px-2 py-1 rounded-full border ${(form.roles || []).includes(r) ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-slate-300'}`}
              data-testid={`vis-role-${r}`}>
              {r}
            </button>
          ))}
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Visible à partir du</Label>
          <Input type="datetime-local" value={toLocalInput(form.visible_from)} onChange={(e) => setForm({ ...form, visible_from: e.target.value })} className="text-sm" data-testid="vis-from" />
        </div>
        <div>
          <Label className="text-xs">Jusqu'au</Label>
          <Input type="datetime-local" value={toLocalInput(form.visible_until)} onChange={(e) => setForm({ ...form, visible_until: e.target.value })} className="text-sm" data-testid="vis-until" />
        </div>
      </div>
      <p className="text-[10px] text-slate-400">Laissez les dates vides pour une visibilité permanente. Fuseau : heure du serveur (UTC).</p>
    </div>
  );
}

const EMPTY_ITEM = { label: '', to: '', icon: 'Sparkles', color: 'blue', emoji: '', highlight: false, divider_before: false, visible: true, audience: 'all', roles: [], visible_from: '', visible_until: '', children: [] };
const EMPTY_MENU = { label: '', icon: 'Sparkles', visible: true, audience: 'all', roles: [], visible_from: '', visible_until: '' };

// ---- Ligne d'élément (sortable) -------------------------------------------
function SortableItemRow({ menu, it, selected, onToggleSelect, onToggleVisible, onEdit, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: it.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1, zIndex: isDragging ? 20 : undefined };
  return (
    <div ref={setNodeRef} style={style}
      className={`flex items-center justify-between gap-2 px-2 py-2 rounded-lg border ${selected ? 'border-indigo-300 bg-indigo-50/50' : 'border-transparent hover:bg-slate-50'} ${it.visible === false ? 'opacity-50' : ''}`}
      data-testid={`item-row-${it.id}`}>
      <div className="flex items-center gap-2 min-w-0">
        <Checkbox checked={selected} onCheckedChange={() => onToggleSelect(menu.id, it.id)} data-testid={`item-select-${it.id}`} />
        <span {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing touch-none text-slate-300 hover:text-slate-600" data-testid={`item-drag-${it.id}`} title="Glisser pour réordonner">
          <GripVertical className="h-4 w-4" />
        </span>
        <IconChip icon={it.icon} color={it.color} size="sm" />
        <div className="min-w-0">
          <div className="text-sm font-medium text-slate-800 truncate flex items-center gap-1.5">
            {it.emoji ? `${it.emoji} ` : ''}{it.label}
            {it.highlight && <Badge variant="outline" className="text-[8px]">accent</Badge>}
          </div>
          <p className="text-[11px] text-slate-400 font-mono truncate flex items-center gap-1"><ExternalLink className="h-2.5 w-2.5" />{it.to || '—'}</p>
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <span className="hidden md:flex items-center gap-1 mr-1"><VisBadges node={it} /></span>
        <button onClick={() => onToggleVisible(menu.id, it)} className="p-1.5 rounded hover:bg-slate-200" data-testid={`item-toggle-${it.id}`} title={it.visible !== false ? 'Masquer' : 'Afficher'}>
          {it.visible !== false ? <Eye className="h-3.5 w-3.5 text-emerald-600" /> : <EyeOff className="h-3.5 w-3.5 text-slate-400" />}
        </button>
        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => onEdit(menu.id, it)} data-testid={`item-edit-${it.id}`}><Pencil className="h-3 w-3" /></Button>
        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 hover:text-red-700" onClick={() => onDelete(menu.id, it)} data-testid={`item-delete-${it.id}`}><Trash2 className="h-3 w-3" /></Button>
      </div>
    </div>
  );
}

// ---- Carte de menu (sortable, repliable) ----------------------------------
function SortableMenuCard({
  menu, collapsed, onToggleCollapse, selected, onToggleSelect, selectedItems, onToggleSelectItem,
  onEditMenu, onDeleteMenu, onToggleMenuVisible, onAddItem, onItemsDragEnd,
  onToggleItemVisible, onEditItem, onDeleteItem, sensors,
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: menu.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.7 : 1, zIndex: isDragging ? 30 : undefined };
  const items = menu.items || [];
  const visibleCount = items.filter((i) => i.visible !== false).length;
  return (
    <Card ref={setNodeRef} style={style} className={`overflow-hidden ${selected ? 'ring-2 ring-indigo-400' : ''} ${menu.visible ? '' : 'opacity-60'}`} data-testid={`menu-card-${menu.key}`}>
      <div className="flex items-center justify-between px-3 py-3 bg-gradient-to-r from-slate-50 to-white border-b">
        <div className="flex items-center gap-2.5 min-w-0">
          <Checkbox checked={selected} onCheckedChange={() => onToggleSelect(menu.id)} data-testid={`menu-select-${menu.key}`} />
          <span {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing touch-none text-slate-300 hover:text-slate-600" data-testid={`menu-drag-${menu.key}`} title="Glisser pour réordonner">
            <GripVertical className="h-5 w-5" />
          </span>
          <button onClick={() => onToggleCollapse(menu.id)} className="text-slate-400 hover:text-slate-700" data-testid={`menu-collapse-${menu.key}`}>
            {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          <IconChip icon={menu.icon} color="slate" />
          <div className="min-w-0">
            <h3 className="font-bold text-slate-900 flex items-center gap-2 flex-wrap">
              {menu.label}
              {menu.is_seed && <Badge variant="outline" className="text-[9px]">seed</Badge>}
              <VisBadges node={menu} />
            </h3>
            <p className="text-[11px] text-slate-400">{items.length} élément(s) · {visibleCount} visible(s)</p>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button onClick={() => onToggleMenuVisible(menu)} className="p-2 rounded hover:bg-slate-200" title={menu.visible ? 'Masquer' : 'Afficher'} data-testid={`menu-toggle-${menu.key}`}>
            {menu.visible ? <Eye className="h-4 w-4 text-emerald-600" /> : <EyeOff className="h-4 w-4 text-slate-400" />}
          </button>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => onEditMenu(menu)} data-testid={`menu-edit-${menu.key}`}><Pencil className="h-3.5 w-3.5" /></Button>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-500 hover:text-red-700" onClick={() => onDeleteMenu(menu)} data-testid={`menu-delete-${menu.key}`}><Trash2 className="h-3.5 w-3.5" /></Button>
        </div>
      </div>
      {!collapsed && (
        <CardContent className="p-2">
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => onItemsDragEnd(menu, e)}>
            <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
              {items.map((it) => (
                <SortableItemRow key={it.id} menu={menu} it={it}
                  selected={selectedItems.has(`${menu.id}::${it.id}`)}
                  onToggleSelect={onToggleSelectItem}
                  onToggleVisible={onToggleItemVisible} onEdit={onEditItem} onDelete={onDeleteItem} />
              ))}
            </SortableContext>
          </DndContext>
          <Button variant="outline" size="sm" className="mt-2 w-full border-dashed" onClick={() => onAddItem(menu.id)} data-testid={`add-item-${menu.key}`}>
            <Plus className="h-3.5 w-3.5 mr-1.5" /> Ajouter un élément
          </Button>
        </CardContent>
      )}
    </Card>
  );
}

export default function FrontOfficeNavPanel() {
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [collapsed, setCollapsed] = useState(new Set());
  const [selectedMenus, setSelectedMenus] = useState(new Set());
  const [selectedItems, setSelectedItems] = useState(new Set());

  const [menuDialog, setMenuDialog] = useState(false);
  const [editingMenu, setEditingMenu] = useState(null);
  const [menuForm, setMenuForm] = useState(EMPTY_MENU);

  const [itemDialog, setItemDialog] = useState(false);
  const [itemMenuId, setItemMenuId] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [itemForm, setItemForm] = useState(EMPTY_ITEM);

  // Verrou de soumission synchrone : empêche tout double POST (double-clic
  // rapide) AVANT que l'état `saving` n'ait eu le temps de désactiver le bouton.
  const submitLock = useRef(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const fetchMenus = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/frontoffice-nav`, { headers: getAuthHeader() });
      setMenus(res.data?.menus || []);
    } catch {
      toast.error('Impossible de charger les menus du front-office');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMenus(); }, [fetchMenus]);

  // ---- Sélection ----
  const toggleSelectMenu = (id) => setSelectedMenus((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleSelectItem = (menuId, itemId) => setSelectedItems((p) => { const k = `${menuId}::${itemId}`; const n = new Set(p); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const clearSelection = () => { setSelectedMenus(new Set()); setSelectedItems(new Set()); };
  const selectionCount = selectedMenus.size + selectedItems.size;

  const toggleCollapse = (id) => setCollapsed((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allCollapsed = menus.length > 0 && menus.every((m) => collapsed.has(m.id));
  const toggleCollapseAll = () => setCollapsed(allCollapsed ? new Set() : new Set(menus.map((m) => m.id)));

  // ---- Drag & drop ----
  const onMenusDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIdx = menus.findIndex((m) => m.id === active.id);
    const newIdx = menus.findIndex((m) => m.id === over.id);
    const next = arrayMove(menus, oldIdx, newIdx);
    setMenus(next);
    try {
      await axios.post(`${API}/admin/frontoffice-nav/reorder`, { order: next.map((m) => m.id) }, { headers: getAuthHeader() });
    } catch { toast.error('Échec du réordonnancement des menus'); fetchMenus(); }
  };

  const onItemsDragEnd = async (menu, { active, over }) => {
    if (!over || active.id === over.id) return;
    const items = menu.items || [];
    const oldIdx = items.findIndex((i) => i.id === active.id);
    const newIdx = items.findIndex((i) => i.id === over.id);
    const nextItems = arrayMove(items, oldIdx, newIdx);
    setMenus((prev) => prev.map((m) => (m.id === menu.id ? { ...m, items: nextItems } : m)));
    try {
      await axios.post(`${API}/admin/frontoffice-nav/${menu.id}/items/reorder`, { order: nextItems.map((i) => i.id) }, { headers: getAuthHeader() });
    } catch { toast.error('Échec du réordonnancement des liens'); fetchMenus(); }
  };

  // ---- Actions de masse ----
  const applyBulk = async (action) => {
    const menuIds = [...selectedMenus];
    const items = [...selectedItems].map((s) => { const [mId, iId] = s.split('::'); return { mId, iId }; });
    if (action === 'delete' && !window.confirm(`Supprimer définitivement ${selectionCount} élément(s) sélectionné(s) ?`)) return;
    setSaving(true);
    try {
      const calls = [];
      for (const id of menuIds) {
        calls.push(action === 'delete'
          ? axios.delete(`${API}/admin/frontoffice-nav/${id}`, { headers: getAuthHeader() })
          : axios.put(`${API}/admin/frontoffice-nav/${id}`, { visible: action === 'show' }, { headers: getAuthHeader() }));
      }
      for (const { mId, iId } of items) {
        calls.push(action === 'delete'
          ? axios.delete(`${API}/admin/frontoffice-nav/${mId}/items/${iId}`, { headers: getAuthHeader() })
          : axios.put(`${API}/admin/frontoffice-nav/${mId}/items/${iId}`, { visible: action === 'show' }, { headers: getAuthHeader() }));
      }
      await Promise.all(calls);
      toast.success(`Action appliquée à ${selectionCount} élément(s)`);
      clearSelection();
      fetchMenus();
    } catch {
      toast.error('Échec (partiel) de l\u2019action de masse');
      fetchMenus();
    } finally { setSaving(false); }
  };

  // ---- Menu CRUD ----
  const openCreateMenu = () => { setEditingMenu(null); setMenuForm(EMPTY_MENU); setMenuDialog(true); };
  const openEditMenu = (m) => { setEditingMenu(m); setMenuForm({ label: m.label, icon: m.icon, visible: m.visible, audience: m.audience || 'all', roles: m.roles || [], visible_from: m.visible_from || '', visible_until: m.visible_until || '' }); setMenuDialog(true); };

  const saveMenu = async () => {
    if (!menuForm.label.trim()) { toast.error('Libellé du menu requis'); return; }
    setSaving(true);
    try {
      if (editingMenu) {
        await axios.put(`${API}/admin/frontoffice-nav/${editingMenu.id}`, menuForm, { headers: getAuthHeader() });
        toast.success('Menu mis à jour');
      } else {
        await axios.post(`${API}/admin/frontoffice-nav`, menuForm, { headers: getAuthHeader() });
        toast.success('Menu créé');
      }
      setMenuDialog(false);
      fetchMenus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Échec de l\u2019enregistrement');
    } finally { setSaving(false); }
  };

  const deleteMenu = async (m) => {
    if (!window.confirm(`Supprimer le menu « ${m.label} » et tous ses éléments ?`)) return;
    try {
      await axios.delete(`${API}/admin/frontoffice-nav/${m.id}`, { headers: getAuthHeader() });
      toast.success('Menu supprimé');
      fetchMenus();
    } catch (e) { toast.error(e.response?.data?.detail || 'Échec de la suppression'); }
  };

  const toggleMenuVisible = async (m) => {
    try {
      await axios.put(`${API}/admin/frontoffice-nav/${m.id}`, { visible: !m.visible }, { headers: getAuthHeader() });
      fetchMenus();
    } catch { toast.error('Échec'); }
  };

  // ---- Item CRUD ----
  const openCreateItem = (menuId) => { setItemMenuId(menuId); setEditingItem(null); setItemForm(EMPTY_ITEM); setItemDialog(true); };
  const openEditItem = (menuId, it) => {
    setItemMenuId(menuId); setEditingItem(it);
    setItemForm({ label: it.label, to: it.to || '', icon: it.icon || 'Sparkles', color: it.color || 'blue', emoji: it.emoji || '', highlight: !!it.highlight, divider_before: !!it.divider_before, visible: it.visible !== false, audience: it.audience || 'all', roles: it.roles || [], visible_from: it.visible_from || '', visible_until: it.visible_until || '', children: it.children || [] });
    setItemDialog(true);
  };

  const saveItem = async () => {
    if (!itemForm.label.trim()) { toast.error('Libellé de l\u2019élément requis'); return; }
    if (submitLock.current) return;
    submitLock.current = true;
    setSaving(true);
    try {
      if (editingItem) {
        await axios.put(`${API}/admin/frontoffice-nav/${itemMenuId}/items/${editingItem.id}`, itemForm, { headers: getAuthHeader() });
        toast.success('Élément mis à jour');
      } else {
        await axios.post(`${API}/admin/frontoffice-nav/${itemMenuId}/items`, itemForm, { headers: getAuthHeader() });
        toast.success('Élément ajouté');
      }
      setItemDialog(false);
      fetchMenus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Échec de l\u2019enregistrement');
    } finally { setSaving(false); submitLock.current = false; }
  };

  const deleteItem = async (menuId, it) => {
    if (!window.confirm(`Supprimer l\u2019élément « ${it.label} » ?`)) return;
    try {
      await axios.delete(`${API}/admin/frontoffice-nav/${menuId}/items/${it.id}`, { headers: getAuthHeader() });
      toast.success('Élément supprimé');
      fetchMenus();
    } catch (e) { toast.error(e.response?.data?.detail || 'Échec'); }
  };

  const toggleItemVisible = async (menuId, it) => {
    try {
      await axios.put(`${API}/admin/frontoffice-nav/${menuId}/items/${it.id}`, { visible: !(it.visible !== false) }, { headers: getAuthHeader() });
      fetchMenus();
    } catch { toast.error('Échec'); }
  };

  return (
    <div className="space-y-6" data-testid="frontoffice-nav-panel">
      <InstitutionalHero
        gradient="bg-gradient-to-br from-slate-900 via-indigo-900 to-blue-900"
        badge="Front-office"
        title="Menus de la navigation publique"
        subtitle="Glissez-déposez pour réordonner, sélectionnez pour des actions de masse. Gérez liens, simulateurs, offres, audiences et planification — sans code."
        icon={Layers}
        actions={
          <Button onClick={openCreateMenu} className="bg-yellow-400 hover:bg-yellow-300 text-slate-900 font-semibold" data-testid="create-menu-btn">
            <Plus className="h-4 w-4 mr-1.5" /> Nouveau menu
          </Button>
        }
        kpis={[
          { label: 'Menus', value: menus.length },
          { label: 'Visibles', value: menus.filter((m) => m.visible).length },
          { label: 'Éléments', value: menus.reduce((s, m) => s + (m.items?.length || 0), 0) },
        ]}
      />

      {/* Barre d'outils : tout déplier/replier */}
      {!loading && menus.length > 0 && (
        <div className="flex items-center justify-between">
          <Button variant="outline" size="sm" onClick={toggleCollapseAll} data-testid="collapse-all-btn">
            {allCollapsed ? <ChevronDown className="h-4 w-4 mr-1.5" /> : <ChevronRight className="h-4 w-4 mr-1.5" />}
            {allCollapsed ? 'Tout déplier' : 'Tout replier'}
          </Button>
          <p className="text-xs text-slate-400">Astuce : glissez la poignée <GripVertical className="h-3 w-3 inline" /> pour réordonner</p>
        </div>
      )}

      {/* Barre d'actions de masse (sticky) */}
      {selectionCount > 0 && (
        <div className="sticky top-2 z-40 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-indigo-200 bg-indigo-600 px-4 py-3 shadow-lg" data-testid="bulk-bar">
          <span className="text-sm font-semibold text-white">{selectionCount} sélectionné(s)</span>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="secondary" onClick={() => applyBulk('show')} disabled={saving} data-testid="bulk-show"><Eye className="h-4 w-4 mr-1.5" />Afficher</Button>
            <Button size="sm" variant="secondary" onClick={() => applyBulk('hide')} disabled={saving} data-testid="bulk-hide"><EyeOff className="h-4 w-4 mr-1.5" />Masquer</Button>
            <Button size="sm" className="bg-red-500 hover:bg-red-600 text-white" onClick={() => applyBulk('delete')} disabled={saving} data-testid="bulk-delete"><Trash2 className="h-4 w-4 mr-1.5" />Supprimer</Button>
            <Button size="sm" variant="ghost" className="text-white hover:bg-indigo-700" onClick={clearSelection} data-testid="bulk-clear"><X className="h-4 w-4" /></Button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-indigo-500" /></div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onMenusDragEnd}>
          <SortableContext items={menus.map((m) => m.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-4">
              {menus.map((m) => (
                <SortableMenuCard
                  key={m.id} menu={m}
                  collapsed={collapsed.has(m.id)} onToggleCollapse={toggleCollapse}
                  selected={selectedMenus.has(m.id)} onToggleSelect={toggleSelectMenu}
                  selectedItems={selectedItems} onToggleSelectItem={toggleSelectItem}
                  onEditMenu={openEditMenu} onDeleteMenu={deleteMenu} onToggleMenuVisible={toggleMenuVisible}
                  onAddItem={openCreateItem} onItemsDragEnd={onItemsDragEnd}
                  onToggleItemVisible={toggleItemVisible} onEditItem={openEditItem} onDeleteItem={deleteItem}
                  sensors={sensors}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* Dialog Menu */}
      <Dialog open={menuDialog} onOpenChange={setMenuDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="menu-form-modal">
          <DialogHeader><DialogTitle>{editingMenu ? 'Modifier le menu' : 'Nouveau menu'}</DialogTitle><DialogDescription>Déroulant affiché dans la barre de navigation publique.</DialogDescription></DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">Libellé *</Label>
              <Input value={menuForm.label} onChange={(e) => setMenuForm({ ...menuForm, label: e.target.value })} placeholder="ex : COPPAM" data-testid="menu-form-label" />
            </div>
            <div>
              <Label className="text-xs">Icône</Label>
              <select value={menuForm.icon} onChange={(e) => setMenuForm({ ...menuForm, icon: e.target.value })} className="w-full h-9 rounded-md border border-slate-300 px-3 text-sm bg-white" data-testid="menu-form-icon">
                {ICONS.map((i) => <option key={i} value={i}>{i}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-2 pt-1">
              <Switch checked={menuForm.visible} onCheckedChange={(v) => setMenuForm({ ...menuForm, visible: v })} data-testid="menu-form-visible" />
              <Label className="text-xs">Visible dans la navbar publique</Label>
            </div>
            <VisibilityFields form={menuForm} setForm={setMenuForm} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMenuDialog(false)}>Annuler</Button>
            <Button onClick={saveMenu} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="menu-form-save">
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}<Save className="h-4 w-4 mr-1.5" />{editingMenu ? 'Mettre à jour' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Item */}
      <Dialog open={itemDialog} onOpenChange={setItemDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="item-form-modal">
          <DialogHeader><DialogTitle>{editingItem ? 'Modifier l\u2019élément' : 'Nouvel élément de menu'}</DialogTitle><DialogDescription>Lien affiché dans le déroulant (page, simulateur ou offre).</DialogDescription></DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">Libellé *</Label>
              <Input value={itemForm.label} onChange={(e) => setItemForm({ ...itemForm, label: e.target.value })} placeholder="ex : Simulateur Habitat Outre-mer" data-testid="item-form-label" />
            </div>
            <div>
              <Label className="text-xs">Lien / route de destination</Label>
              <Input value={itemForm.to} onChange={(e) => setItemForm({ ...itemForm, to: e.target.value })} placeholder="ex : /simulateur-habitat" className="font-mono text-sm" data-testid="item-form-to" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Icône</Label>
                <select value={itemForm.icon} onChange={(e) => setItemForm({ ...itemForm, icon: e.target.value })} className="w-full h-9 rounded-md border border-slate-300 px-3 text-sm bg-white" data-testid="item-form-icon">
                  {ICONS.map((i) => <option key={i} value={i}>{i}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs">Couleur</Label>
                <select value={itemForm.color} onChange={(e) => setItemForm({ ...itemForm, color: e.target.value })} className="w-full h-9 rounded-md border border-slate-300 px-3 text-sm bg-white" data-testid="item-form-color">
                  {COLORS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Emoji (optionnel, préfixe)</Label>
              <Input value={itemForm.emoji} onChange={(e) => setItemForm({ ...itemForm, emoji: e.target.value })} placeholder="🏠" maxLength={4} data-testid="item-form-emoji" />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={itemForm.highlight} onCheckedChange={(v) => setItemForm({ ...itemForm, highlight: v })} data-testid="item-form-highlight" />
              <Label className="text-xs">Style accentué (gras + couleur)</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={itemForm.divider_before} onCheckedChange={(v) => setItemForm({ ...itemForm, divider_before: v })} data-testid="item-form-divider" />
              <Label className="text-xs">Séparateur au-dessus</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={itemForm.visible} onCheckedChange={(v) => setItemForm({ ...itemForm, visible: v })} data-testid="item-form-visible" />
              <Label className="text-xs">Visible</Label>
            </div>
            <VisibilityFields form={itemForm} setForm={setItemForm} />

            <NavItemChildrenEditor itemForm={itemForm} setItemForm={setItemForm} icons={ICONS} colors={COLORS} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setItemDialog(false)}>Annuler</Button>
            <Button onClick={saveItem} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="item-form-save">
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}<Save className="h-4 w-4 mr-1.5" />{editingItem ? 'Mettre à jour' : 'Ajouter'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
